import { Fragment, useEffect, useRef, useState } from 'react';
import { MenuItem, TextField, Tooltip } from '@mui/material';
// import { MdHelp } from 'react-icons/md';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Form from '@rjsf/mui';
import { RegistryWidgetsType, WidgetProps, asNumber, guessType } from '@rjsf/utils';
import { MdOutlineClose } from 'react-icons/md';
import useGetSurvivorsConversation from '../../modules/datastores/useGetSurvivorsConversation';
import { useGetForm } from '../../modules/datastores/useGetForm';
import { useSubmitVisitorFeedback } from '../../modules/datastores/useGetMessages';
import { uiStore } from '../../modules/datastores/useUiStore';
import { FormInfo } from './FormInfo';
import Typography from '@mui/material/Typography';
import validator from '@rjsf/validator-ajv8';

interface FeedbackFormProps {
  form_id: string;
  dialog_open: boolean;
  task_sid: string;
  webchat_language?: string;
}
const FeedbackForm: React.FC<FeedbackFormProps> = ({ form_id, dialog_open, task_sid, webchat_language = 'en' }) => {
  const { data: formsQuery, isSuccess } = useGetForm(form_id);
  const [open, SetOpen] = useState<boolean>(false);
  const { mutateAsync: mutateVisitorFormSubmission } = useSubmitVisitorFeedback();
  const { userQuickExit } = useGetSurvivorsConversation();
  const isApiCalled = useRef(false);
  const customWidgets: RegistryWidgetsType = {
    TextareaWidget: TextareaWidget as any,
    SelectWidget: SelectWidget as any,
  };

  useEffect(() => SetOpen(dialog_open && !userQuickExit), [dialog_open, userQuickExit]);

  /**
   *!form submit function
   * @param formData
   */
  const submitFeedback = ({ formData }: any) => {
    isApiCalled.current = true;
    const updated_feedback = {
      form_id,
      identity: task_sid,
      submission: {
        task_sid,
        formData,
      },
      complete_request: true,
      display_name: task_sid,
    };
    mutateVisitorFormSubmission(updated_feedback, {
      onSuccess: () => {
        uiStore.setSuccessMessage('Thank you for your feedback!');
        SetOpen(false);
      },
    });
  };
  const onError = (errors: unknown) => {
    //console.log(errors);
    return errors;
  };
  return (
    <Fragment>
      {isSuccess && form_id !== '' && formsQuery?.fields && formsQuery.field_specs && (
        <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description" className="width-set w-full fix-width-set">
          <DialogTitle id="alert-dialog-title">
            {webchat_language === 'en' ? 'Chat Comment Card' : 'Tarjeta Para Comentarios'}
            <IconButton onClick={() => SetOpen(false)} className="float-right">
              <MdOutlineClose />
            </IconButton>
          </DialogTitle>
          <DialogContent className="middle-min-600">
            <Typography variant="h2" className="text-black pt-3 text-lg">
              {webchat_language === 'en'
                ? 'Please help us make this service better by sharing some information about your hotline experience today. All questions are optional and all answers are anonymous and confidential. Thank you.'
                : 'Por favor ayúdenos a mejorar este servicio con compartir información sobre su experiencia con la línea directa. Todas las preguntas son opcionales y todas las resp uestas son anónimas y confidenciales. Gracias.'}
            </Typography>
            <FormInfo />
            <Form
              validator={validator}
              schema={formsQuery?.fields}
              widgets={customWidgets as any}
              showErrorList={false}
              onError={onError}
              noHtml5Validate
              uiSchema={formsQuery?.field_specs}
              onSubmit={submitFeedback}
            >
              <DialogActions>
                <Button disabled={isApiCalled.current} className="button-primary mr-2" type="submit">
                  Submit Feedback
                </Button>
                <Button className="button-secondary" onClick={() => SetOpen(false)}>
                  Exit
                </Button>
              </DialogActions>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </Fragment>
  );
};

export default FeedbackForm;

export const TextareaWidget = ({ id, placeholder, value, required, disabled, autofocus, label, readonly, onBlur, onFocus, onChange, options, schema, rawErrors = [] }: WidgetProps) => {
  const _onChange = ({ target: { value: val } }: React.ChangeEvent<HTMLInputElement>) => onChange(val === '' ? options.emptyValue : val);
  const _onBlur = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onBlur(id, val);
  const _onFocus = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, val);

  let rows: string | number = 5;
  if (typeof options.rows === 'string' || typeof options.rows === 'number') {
    // eslint-disable-next-line prefer-destructuring
    rows = options.rows;
  }

  return (
    <TextField
      id={id}
      label={label || schema.title}
      placeholder={label || schema.title || placeholder}
      disabled={disabled || readonly}
      value={value}
      required={required}
      autoFocus={autofocus}
      multiline={true}
      rows={rows}
      error={rawErrors.length > 0}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      InputLabelProps={{
        shrink: true,
        style: { pointerEvents: 'auto' },
      }}
    />
  );
};

export const SelectWidget = ({ schema, id, options, label, required, disabled, readonly, value, multiple, autofocus, onChange, onBlur, onFocus, rawErrors = [] }: WidgetProps) => {
  const { enumOptions, enumDisabled } = options;

  const emptyValue = multiple ? [] : '';

  const nums = new Set(['number', 'integer']);
  // const IconWithTooltip = (props: any) => (
  //   <Tooltip title={props?.title} placement="bottom-start">
  //     <span>
  //       <MdHelp />
  //     </span>
  //   </Tooltip>
  // );

  /**
   * This is a silly limitation in the DOM where option change event values are
   * always retrieved as strings.
   */
  const processValue = (schema: any, value: any) => {
    // "enum" is a reserved word, so only "type" and "items" can be destructured
    const { type, items } = schema;
    if (value === '') {
      return undefined;
    } else if (type === 'array' && items && nums.has(items.type)) {
      return value.map(asNumber);
    } else if (type === 'boolean') {
      return value === 'true';
    } else if (type === 'number') {
      return asNumber(value);
    }

    // If type is undefined, but an enum is present, try and infer the type from
    // the enum values
    if (schema.enum) {
      if (schema.enum.every((x: any) => guessType(x) === 'number')) {
        return asNumber(value);
      } else if (schema.enum.every((x: any) => guessType(x) === 'boolean')) {
        return value === 'true';
      }
    }

    return value;
  };

  const _onChange = ({ target: { value: val } }: React.ChangeEvent<{ name?: string; value: unknown }>) => onChange(processValue(schema, val));
  const _onBlur = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onBlur(id, processValue(schema, val));
  const _onFocus = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, processValue(schema, val));

  return (
    <TextField
      id={id}
      select
      value={typeof value === 'undefined' ? emptyValue : value}
      required={required}
      label={
        <Tooltip title={label || (schema.title as any)} arrow enterTouchDelay={0}>
          <span>{label || schema.title}</span>
        </Tooltip>
      }
      disabled={disabled || readonly}
      error={rawErrors.length > 0}
      autoFocus={autofocus}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      InputLabelProps={{
        shrink: true,
      }}
      SelectProps={{
        multiple: typeof multiple === 'undefined' ? false : multiple,
      }}
    >
      {(enumOptions as any).map(({ value: val, label: lbl }: any, i: number) => {
        const _disabled: any = enumDisabled && (enumDisabled as any).indexOf(val) != -1;
        return (
          <MenuItem key={i} value={val} disabled={_disabled}>
            {lbl}
          </MenuItem>
        );
      })}
    </TextField>
  );
};
